import cx from 'classnames'
import { ExternalIntegrationEnum, YouTubeIntegration } from 'api/clients/ExternalIntegrationsClient'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { highlightContent } from '../highlightContent'
import { Button } from 'blaze-ui/Button'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { ScheduledPostShow, YoutubePostSettings } from 'api/clients/ScheduledPostsClient'
import { useTruncateText } from '../hooks/useTruncateText'
import { MoreButton } from '../InstagramReelPreview/Footer'
import Styles from './YouTubeShortPreview.module.scss'

interface FooterProps {
  integration?: YouTubeIntegration
  post?: ScheduledPostShow
  content?: string
  isGenerating?: boolean
  alwaysTruncateCaption?: boolean
}

export const Footer = ({
  integration,
  post,
  content: contentProp,
  isGenerating = false,
  alwaysTruncateCaption = false,
}: FooterProps) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const target = post?.scheduled_post_targets.find((target) => target.provider === 'youtube')
  const postSettings = target?.target_overrides?.post_settings as YoutubePostSettings
  const title = postSettings?.title || post?.source_title || contentProp || ''

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: title,
    truncatedByDefault: alwaysTruncateCaption,
  })

  const showMoreButton = title && shouldShowMoreButton

  const postContent = post?.scheduled_post_targets.find((target) => target.provider === ExternalIntegrationEnum.YouTube)
    ?.target_overrides?.content

  return (
    <div className={Styles.footer}>
      <div className={Styles.videoInfo}>
        <div className={Styles.profileInfo}>
          <img src={avatar || Silhouette} alt={username} className={Styles.avatar} />
          <div className={Styles.username}>{`@${name}`}</div>
          <Button variant="tertiary" className={Styles.subscribeButton} size="xs" data-testid="subscribe-button">
            Subscribe
          </Button>
        </div>
        <div className={Styles.caption}>
          {isGenerating ? (
            <CaptionSkeleton />
          ) : (
            <>
              <span
                ref={textRef as React.RefObject<HTMLSpanElement>}
                key={`caption-${title}`}
                className={cx({ [Styles.truncated]: isTruncated })}
                dangerouslySetInnerHTML={{
                  __html: highlightContent(postContent || post?.content || contentProp || title, highlightClassNames),
                }}
              />
              {showMoreButton && !alwaysTruncateCaption && isTruncated && (
                <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
              )}
            </>
          )}
        </div>
        {!isGenerating && showMoreButton && !alwaysTruncateCaption && !isTruncated && (
          <div className={Styles.lessButton}>
            <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
          </div>
        )}
      </div>
    </div>
  )
}
